import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, Battle, VoteResult } from '../api/client';
import ShotTimeline from '../components/ShotTimeline';
import SampleClipPicker from '../components/SampleClipPicker';
import ShareButtons from '../components/ShareButtons';

type VoteChoice = 'model_a' | 'model_b' | 'tie' | 'skip';
type PageMode = 'pick' | 'battle';

export default function BattlePage() {
  const navigate = useNavigate();
  const { battleId } = useParams();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<PageMode>(battleId ? 'battle' : 'pick');
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [currentBattle, setCurrentBattle] = useState<Battle | null>(null);
  const [voteResult, setVoteResult] = useState<VoteResult | null>(null);
  const [isVoting, setIsVoting] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [syncPlayback, setSyncPlayback] = useState(true);
  const [videoATime, setVideoATime] = useState(0);
  const [videoBTime, setVideoBTime] = useState(0);
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);

  // Check for quick=true query param to skip clip picker
  const quickMode = searchParams.get('quick') === 'true';

  // Create new battle
  const createBattleMutation = useMutation({
    mutationFn: (clipId?: string) => api.createBattle(clipId || undefined),
    onSuccess: (data) => {
      setCurrentBattle(data);
      setVoteResult(null);
      setMode('battle');
      navigate(`/battle/${data.battle_id}`, { replace: true });
    },
  });

  // Get existing battle
  const { isLoading: isFetchingBattle } = useQuery({
    queryKey: ['battle', battleId],
    queryFn: () => api.getBattle(battleId!),
    enabled: !!battleId && !currentBattle,
    // @ts-ignore - onSuccess is valid in react-query v5
    select: (data) => {
      setCurrentBattle(data);
      return data;
    },
  });

  // Submit vote
  const voteMutation = useMutation({
    mutationFn: ({ battleId, winner }: { battleId: string; winner: VoteChoice }) =>
      api.submitVote(battleId, winner),
    onSuccess: (data) => {
      setVoteResult(data);
      setVoteError(null);
      setIsVoting(false);
    },
    onError: (error) => {
      setVoteError(error instanceof Error ? error.message : 'Failed to submit vote. Please try again.');
      setIsVoting(false);
    },
  });

  // Create initial battle if in quick mode (skip clip picker)
  useEffect(() => {
    if (quickMode && !battleId && !currentBattle && !createBattleMutation.isPending) {
      createBattleMutation.mutate(undefined);
    }
  }, [quickMode, battleId, currentBattle]);

  // Start battle with selected clip
  const handleStartBattle = () => {
    createBattleMutation.mutate(selectedClipId || undefined);
  };

  // Sync video playback
  const handleVideoPlay = (source: 'a' | 'b') => {
    if (!syncPlayback) return;
    const sourceVideo = source === 'a' ? videoARef.current : videoBRef.current;
    const targetVideo = source === 'a' ? videoBRef.current : videoARef.current;
    if (sourceVideo && targetVideo) {
      targetVideo.currentTime = sourceVideo.currentTime;
      targetVideo.play();
    }
  };

  const handleVideoPause = (source: 'a' | 'b') => {
    if (!syncPlayback) return;
    const targetVideo = source === 'a' ? videoBRef.current : videoARef.current;
    if (targetVideo) {
      targetVideo.pause();
    }
  };

  // Handle timeupdate for timeline sync
  const handleTimeUpdate = useCallback((source: 'a' | 'b') => {
    const video = source === 'a' ? videoARef.current : videoBRef.current;
    if (video) {
      if (source === 'a') {
        setVideoATime(video.currentTime);
      } else {
        setVideoBTime(video.currentTime);
      }
    }
  }, []);

  // Seek to specific time
  const handleSeek = useCallback((source: 'a' | 'b', time: number) => {
    const video = source === 'a' ? videoARef.current : videoBRef.current;
    if (video) {
      video.currentTime = time;
      if (syncPlayback) {
        const otherVideo = source === 'a' ? videoBRef.current : videoARef.current;
        if (otherVideo) {
          otherVideo.currentTime = time;
        }
      }
    }
  }, [syncPlayback]);

  // Play both videos simultaneously
  const handlePlayBoth = useCallback(() => {
    if (videoARef.current && videoBRef.current) {
      // Sync times first
      const syncTime = Math.min(
        videoARef.current.currentTime,
        videoBRef.current.currentTime
      );
      videoARef.current.currentTime = syncTime;
      videoBRef.current.currentTime = syncTime;
      // Then play both
      videoARef.current.play();
      videoBRef.current.play();
    }
  }, []);

  const handleVote = (choice: VoteChoice) => {
    if (!currentBattle || isVoting) return;
    setIsVoting(true);
    setVoteError(null);
    voteMutation.mutate({ battleId: currentBattle.battle_id, winner: choice });
  };

  const handleNextBattle = () => {
    setCurrentBattle(null);
    setVoteResult(null);
    setSelectedClipId(null);
    setMode('pick');
    navigate('/battle', { replace: true });
  };

  const handleQuickNextBattle = () => {
    setCurrentBattle(null);
    setVoteResult(null);
    createBattleMutation.mutate(undefined);
  };

  // Clip picker mode
  if (mode === 'pick' && !battleId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Choose Your Battle</h1>
          <p className="text-arena-muted">
            Select a video clip to compare scene detection models
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="card p-6 mb-6">
            <SampleClipPicker
              onSelect={setSelectedClipId}
              selectedClipId={selectedClipId}
            />
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleStartBattle}
              disabled={createBattleMutation.isPending}
              className="btn btn-primary text-lg px-10 py-4"
            >
              {createBattleMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Preparing Battle...
                </span>
              ) : (
                'Start Battle'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (createBattleMutation.isPending || isFetchingBattle) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-pulse">⚔️</div>
          <h2 className="text-2xl font-bold mb-2">Preparing Battle...</h2>
          <p className="text-arena-muted mb-4">Loading video clips and detection results</p>
          <div className="flex justify-center">
            <div className="w-8 h-8 border-4 border-arena-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (createBattleMutation.isError) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-2xl font-bold mb-2">No Battles Available</h2>
          <p className="text-arena-muted mb-6">
            We need more video clips before battles can begin.
          </p>
          <button onClick={handleNextBattle} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!currentBattle) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Scene Detection Battle</h1>
        <p className="text-arena-muted">
          Which AI detected shot boundaries more accurately?
        </p>
      </div>

      {/* Video Comparison */}
      <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-5xl mx-auto">
        {/* Model A */}
        <div className="card p-4">
          <div className="text-center mb-4">
            <span className="inline-block px-4 py-1 bg-arena-accent/20 text-arena-accent rounded-full text-sm font-medium">
              {voteResult ? voteResult.reveal.model_a.display_name : 'Anonymous Model 1'}
            </span>
          </div>
          <div className="aspect-video bg-black rounded-xl overflow-hidden mb-4">
            <video
              ref={videoARef}
              src={currentBattle.video_url}
              className="w-full h-full object-contain"
              controls
              crossOrigin="anonymous"
              preload="metadata"
              onPlay={() => handleVideoPlay('a')}
              onPause={() => handleVideoPause('a')}
              onTimeUpdate={() => handleTimeUpdate('a')}
              onError={(e) => console.error('Video A error:', e)}
              playsInline
            />
          </div>
          <ShotTimeline
            shots={currentBattle.model_a.shots || []}
            duration={currentBattle.duration_seconds}
            currentTime={videoATime}
            onSeek={(time) => handleSeek('a', time)}
            color="cyan"
          />
          <div className="text-center text-sm text-arena-muted mt-2">
            {currentBattle.model_a.shots?.length || 0} shots detected
          </div>
        </div>

        {/* Model B */}
        <div className="card p-4">
          <div className="text-center mb-4">
            <span className="inline-block px-4 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
              {voteResult ? voteResult.reveal.model_b.display_name : 'Anonymous Model 2'}
            </span>
          </div>
          <div className="aspect-video bg-black rounded-xl overflow-hidden mb-4">
            <video
              ref={videoBRef}
              src={currentBattle.video_url}
              className="w-full h-full object-contain"
              controls
              crossOrigin="anonymous"
              preload="metadata"
              onPlay={() => handleVideoPlay('b')}
              onPause={() => handleVideoPause('b')}
              onTimeUpdate={() => handleTimeUpdate('b')}
              onError={(e) => console.error('Video B error:', e)}
              playsInline
            />
          </div>
          <ShotTimeline
            shots={currentBattle.model_b.shots || []}
            duration={currentBattle.duration_seconds}
            currentTime={videoBTime}
            onSeek={(time) => handleSeek('b', time)}
            color="purple"
          />
          <div className="text-center text-sm text-arena-muted mt-2">
            {currentBattle.model_b.shots?.length || 0} shots detected
          </div>
        </div>
      </div>

      {/* Playback Controls */}
      <div className="flex justify-center items-center gap-4 mb-8">
        <button
          onClick={handlePlayBoth}
          className="btn btn-secondary flex items-center gap-2"
        >
          <span>▶</span> Play Both
        </button>
        <label className="flex items-center gap-2 text-sm text-arena-muted cursor-pointer">
          <input
            type="checkbox"
            checked={syncPlayback}
            onChange={(e) => setSyncPlayback(e.target.checked)}
            className="w-4 h-4 rounded border-arena-border bg-arena-surface accent-arena-accent"
          />
          Sync Playback
        </label>
      </div>

      {/* Detection Warning */}
      {(currentBattle.model_a.shots?.length === 0 && currentBattle.model_b.shots?.length === 0) && (
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 px-4 py-3 rounded-lg text-sm text-center">
            Detection results may still be processing. Timelines will update when ready.
          </div>
        </div>
      )}

      {/* Voting Buttons or Results */}
      <div className="max-w-2xl mx-auto">
        {!voteResult ? (
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-center mb-6">
              Which model found shot boundaries better?
            </h3>
            {voteError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm text-center">
                {voteError}
              </div>
            )}
            {isVoting ? (
              <div className="flex flex-col items-center py-8">
                <div className="w-8 h-8 border-4 border-arena-accent border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-arena-muted">Submitting vote...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <button
                    onClick={() => handleVote('model_a')}
                    className="btn-vote bg-arena-accent/20 text-arena-accent border border-arena-accent/30 hover:bg-arena-accent/30"
                  >
                    Model 1 is better
                  </button>
                  <button
                    onClick={() => handleVote('model_b')}
                    className="btn-vote bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30"
                  >
                    Model 2 is better
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleVote('tie')}
                    className="btn btn-secondary"
                  >
                    Tie - Both equal
                  </button>
                  <button
                    onClick={() => handleVote('skip')}
                    className="btn btn-secondary"
                  >
                    Skip - Can't decide
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="card p-6">
            <div className="text-center mb-6">
              <div className="text-4xl mb-2">
                {voteResult.your_vote === 'model_a' || voteResult.your_vote === 'model_b'
                  ? '✅'
                  : voteResult.your_vote === 'tie'
                  ? '🤝'
                  : '⏭️'}
              </div>
              <h3 className="text-xl font-bold">
                {voteResult.your_vote === 'model_a'
                  ? `You voted for ${voteResult.reveal.model_a.display_name}`
                  : voteResult.your_vote === 'model_b'
                  ? `You voted for ${voteResult.reveal.model_b.display_name}`
                  : voteResult.your_vote === 'tie'
                  ? 'You called it a tie'
                  : 'Battle skipped'}
              </h3>
            </div>

            {/* Model Reveal */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-arena-surface rounded-xl p-4 text-center">
                <div className="text-sm text-arena-muted mb-1">Model 1</div>
                <div className="font-semibold">{voteResult.reveal.model_a.display_name}</div>
                <div className="text-arena-accent font-mono mt-2">
                  ELO: {voteResult.reveal.model_a.elo_rating}
                </div>
              </div>
              <div className="bg-arena-surface rounded-xl p-4 text-center">
                <div className="text-sm text-arena-muted mb-1">Model 2</div>
                <div className="font-semibold">{voteResult.reveal.model_b.display_name}</div>
                <div className="text-purple-400 font-mono mt-2">
                  ELO: {voteResult.reveal.model_b.elo_rating}
                </div>
              </div>
            </div>

            {/* Head-to-Head Stats */}
            <div className="bg-arena-surface rounded-xl p-4 mb-6">
              <div className="text-sm text-arena-muted text-center mb-3">
                Head-to-head ({voteResult.stats.total_votes} votes)
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium w-12 text-right">
                  {voteResult.stats.model_a_wins_percent}%
                </div>
                <div className="flex-1 h-3 bg-arena-border rounded-full overflow-hidden flex">
                  <div
                    className="bg-arena-accent h-full"
                    style={{ width: `${voteResult.stats.model_a_wins_percent}%` }}
                  />
                  <div
                    className="bg-arena-muted h-full"
                    style={{ width: `${voteResult.stats.ties_percent}%` }}
                  />
                  <div
                    className="bg-purple-500 h-full"
                    style={{ width: `${voteResult.stats.model_b_wins_percent}%` }}
                  />
                </div>
                <div className="text-sm font-medium w-12">
                  {voteResult.stats.model_b_wins_percent}%
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <ShareButtons
              battleId={currentBattle.battle_id}
              modelAName={voteResult.reveal.model_a.display_name}
              modelBName={voteResult.reveal.model_b.display_name}
              userVote={voteResult.your_vote}
            />

            {/* ContentIndexer CTA */}
            <div className="bg-gradient-to-r from-arena-accent/10 to-purple-500/10 border border-arena-accent/20 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-4">
                <div className="text-3xl">🎬</div>
                <div className="flex-1">
                  <div className="font-semibold text-sm mb-1">
                    Want shot detection on YOUR videos?
                  </div>
                  <div className="text-arena-muted text-xs">
                    ContentIndexer analyzes your videos with multiple AI models automatically.
                  </div>
                </div>
                <a
                  href="https://contentindexer.com?ref=arena"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary text-sm whitespace-nowrap"
                >
                  Try Free →
                </a>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleQuickNextBattle}
                disabled={createBattleMutation.isPending}
                className="btn btn-primary flex-1"
              >
                {createBattleMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Loading...
                  </span>
                ) : (
                  'Quick Next →'
                )}
              </button>
              <button
                onClick={handleNextBattle}
                className="btn btn-secondary"
              >
                Choose Clip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
